import React, { useState, useEffect, useRef } from "react";

/** Nothing may stay hidden longer than this if the observer looks broken. */
const FAILSAFE_MS = 1200;

/**
 * Set the first time any Reveal on the page is told it is on screen, which
 * proves the observer is delivering callbacks. Until that happens we cannot
 * tell "below the fold" apart from "never going to fire".
 */
let observerWorks = false;

/**
 * Fades and lifts its children into place the first time they scroll into
 * view. `delay` staggers siblings. Motion is disabled entirely by the
 * prefers-reduced-motion rule on .mw-reveal.
 *
 * The reveal is an enhancement, never a gate: .mw-reveal starts at opacity 0,
 * so anything that stops the observer from firing — no IntersectionObserver,
 * a callback missed while the tab was backgrounded, a half-applied hot
 * reload — would otherwise leave the content invisible for good. If no Reveal
 * anywhere has been revealed by FAILSAFE_MS, we assume that has happened and
 * show the content. When the observer is healthy the failsafe does nothing,
 * so scroll reveals further down the page still play normally.
 */
function Reveal({
  children,
  delay = 0,
  className = "",
  as: Tag = "div",
  /* Anything else lands on the rendered element. The leadership rail needs a
     Reveal that is also the tablist — a wrapper in between would break the
     tablist/tab relationship — so the ARIA attributes and the key handler
     have to reach the element this renders. */
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observerWorks = true;
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );

    observer.observe(node);

    const failsafe = setTimeout(() => {
      if (observerWorks) return;
      setShown(true);
      observer.disconnect();
    }, FAILSAFE_MS);

    return () => {
      clearTimeout(failsafe);
      observer.disconnect();
    };
  }, []);

  return (
    <Tag
      ref={ref}
      className={`mw-reveal ${shown ? "mw-reveal-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default Reveal;
